// Barra "Hay una versión nueva de SmartDx" (#1451 · R3).
//
// Aparece cuando `helpers/version-nueva.js` detecta que el deploy cambió
// mientras la pestaña estaba abierta. NO bloquea nada y la app NUNCA se recarga
// sola por versión nueva (v3, R2 descartado: perdía trabajo que no nace de una
// tecla). La única vía es el botón "Actualizar", que guarda antes lo pendiente.
//
// Posición (v2, 26-sep-2026): ARRIBA, nunca encima de un campo de texto. La v1
// iba abajo y en 1280 tapaba el borde inferior de la Diaria sin poder cerrarse
// (hallazgo del revisor). La franja superior es la única zona que en todas las
// vistas es navegación y no texto editable:
//   · lg+ (≥1200, el PC del hospital): arriba a la izquierda, en el hueco entre
//     el botón volver y la pill del navbar. No tapa nada.
//   · < lg (celular, tablet): franja superior a lo ancho, sobre el navbar (que
//     no es editable). El cockpit mobile arranca a 60 px, la barra mide ≤ 52.
// zIndex 1150: sobre el navbar (1100), bajo sheets (1200) y diálogos (1300),
// así que cualquier sheet o diálogo (donde viven los "Aplicar") la tapa a ella.
// La × la esconde hasta la próxima versión detectada (R3 v2).

import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import {
  actualizarAhora,
  descartarVersionNueva,
  getVersionDescartada,
  getVersionNueva,
  suscribirVersionNueva,
} from "../helpers/version-nueva";

const foto = () => ({ version: getVersionNueva(), descartada: getVersionDescartada() });

export default function VersionNuevaBar() {
  const [{ version, descartada }, setFoto] = useState(foto);
  const [actualizando, setActualizando] = useState(false);

  useEffect(() => suscribirVersionNueva(() => setFoto(foto())), []);

  if (!version || descartada === version.nueva) return null;

  const onActualizar = () => {
    setActualizando(true);
    actualizarAhora().finally(() => setActualizando(false));
  };

  return (
    <Box
      data-testid="version-nueva"
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        zIndex: 1150,
        top: { xs: "max(4px, env(safe-area-inset-top))", lg: "12px" },
        left: { xs: "max(8px, env(safe-area-inset-left))", lg: "68px" },
        right: { xs: "max(8px, env(safe-area-inset-right))", lg: "auto" },
        // lg+: hasta donde empieza la pill del navbar (centrada, ~280 px).
        maxWidth: { lg: "calc(50vw - 220px)" },
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: 1.25,
        pr: 0.5,
        py: 0.5,
        minHeight: 48,
        maxHeight: 52,
        boxSizing: "border-box",
        borderRadius: "14px",
        backgroundColor: "var(--bg-elevated)",
        backgroundImage: "none",
        border: "1px solid rgba(var(--fg-rgb),0.14)",
        boxShadow: "0 12px 32px rgba(var(--shadow-rgb),0.45)",
        color: "var(--text-primary)",
        animation: "versionNuevaIn 260ms cubic-bezier(0.22, 1, 0.36, 1)",
        "@keyframes versionNuevaIn": {
          from: { opacity: 0, translate: "0 -8px" },
          to: { opacity: 1, translate: "0 0" },
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          flex: "0 0 30px",
          width: 30,
          height: 30,
          borderRadius: "9px",
          display: "grid",
          placeItems: "center",
          backgroundColor: "rgba(var(--color-primary-rgb),0.14)",
          color: "var(--color-primary)",
        }}
      >
        <SystemUpdateAltOutlinedIcon sx={{ fontSize: 18 }} />
      </Box>
      <Typography
        component="span"
        sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}
      >
        Hay una versión nueva de SmartDx
      </Typography>
      <Button
        data-testid="version-nueva-actualizar"
        onClick={onActualizar}
        disabled={actualizando}
        sx={{
          flexShrink: 0,
          minHeight: 40,
          "@media (pointer: coarse)": { minHeight: 44 },
          px: 1.5,
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "10px",
          color: "var(--text-on-primary)",
          backgroundColor: "var(--color-primary)",
          "&:hover": { backgroundColor: "var(--color-primary-soft)" },
          "&.Mui-disabled": {
            color: "var(--text-on-primary)",
            backgroundColor: "var(--color-primary)",
            opacity: 0.7,
          },
        }}
      >
        Actualizar
      </Button>
      <IconButton
        data-testid="version-nueva-cerrar"
        aria-label="Cerrar aviso de versión nueva"
        onClick={descartarVersionNueva}
        size="small"
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          // Regla del repo: 44 px mínimo en touch (el revisor midió 36).
          "@media (pointer: coarse)": { width: 44, height: 44 },
          // El override global de MuiIconButton lo pinta teal relleno: acá es ghost.
          backgroundColor: "transparent",
          color: "var(--text-muted)",
          "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.08)" },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}
